import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useEarningsInsightsFilter } from '~/hooks/use-earnings-insights-filter/useEarningsInsightsFilter';
import { useEntity } from '~/hooks/use-entities';
import { useGetEarningById } from '~/hooks/use-get-earning-by-id/useGetEarningById';
import { useGetEarningsInsightRecap } from '~/hooks/use-get-earnings-insight-recap/useGetEarningsInsightRecap';
import { ImpactedCompanies } from '~/modules/impacted-companies/ImpactedCompanies';
import { ThematicFocus } from '~/modules/thematic-focus/ThematicFocus';
import { MarketReactionSection } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/earnings-recap/components/market-reaction-section/MarketReactionSection';

import { TextSection } from '../../../../components/text-section/TextSection';
import { useEarningsInsightDrawer } from '../../useEarningsInsightDrawer';
import { BullBearPoints } from '../components/bull-bear-points/BullBearPoints';
import { EarningsInsightHeader } from '../components/earnings-insight-header/EarningsInsightHeader';
import { EarningsInsightTypePill } from '../components/earnings-insight-type-pill/EarningsInsightTypePill';
import { Sidebar } from '../components/side-bar/Sidebar';
import { useSidebarItems } from '../components/side-bar/useSidebarItems';
import * as Shared from '../EarningsInsightWidget.styles';
import { DocumentsAndCitations } from './components/documents-and-citations/DocumentsAndCitations';
import { EarningsHighlightsTable } from './components/earnings-highlights-table/EarningsHighlightsTable';
import { KpisTable } from './components/KpisTable/KpisTable';
import { ManagementCommentarySection } from './components/management-commentary-section/ManagementCommentarySection';
import { RecapOverview } from './components/recap-overview/RecapOverview';
import * as S from './EarningsRecap.styles';

export const EarningsRecap = () => {
  const { t } = useTranslation(['earnings']);
  const { earningsId, activeEntityTag } = useEarningsInsightDrawer();
  const containerRef = useRef<HTMLDivElement>(null);
  const { recapItems } = useSidebarItems();

  const {
    data: entityData,
    isFetching: isEntityFetching,
    isError: isEntityError,
    error: entityError,
  } = useEntity(activeEntityTag ?? '');

  const {
    data: earningsInsightsData,
    isFetching: isEarningsInsightsFetching,
    isError: isEarningsInsightsError,
    error: earningsInsightsError,
  } = useEarningsInsightsFilter({
    body: {
      earnings_ids: [earningsId ?? ''],
    },
    isEnabled: !!earningsId,
  });

  const {
    data: earningsData,
    isFetching: isEarningsFetching,
    isError: isEarningsError,
    error: earningsError,
  } = useGetEarningById(earningsId ?? '');

  const { data: earningsRecapData, isFetching: isEarningsRecapFetching } =
    useGetEarningsInsightRecap(
      earningsInsightsData?.find(insight => insight.type === 'recap')?.id ?? ''
    );

  const isLoading =
    isEarningsFetching ||
    isEarningsRecapFetching ||
    isEarningsInsightsFetching ||
    isEntityFetching;

  const isError = isEarningsError || isEarningsInsightsError || isEntityError;

  if (isLoading) {
    return <Shared.StyledLoader />;
  }

  if (isError || !entityData?.data) {
    throw new Error(
      earningsError?.message ||
        earningsInsightsError?.message ||
        entityError?.message
    );
  }

  const isReported = earningsData?.reported;

  if (!isReported) {
    return (
      <Shared.Container>
        <Shared.EmptyWrapper>
          <Shared.StyledImg
            alt=""
            src="/app/static/images/empty-state/radio-telescope.png"
            height={130}
            width={146}
          />
          <Shared.EmptyTitle>
            {t('earnings:earningsInsight.drawer.earningsRecap.emptyTitle')}
          </Shared.EmptyTitle>
        </Shared.EmptyWrapper>
      </Shared.Container>
    );
  }

  const entity = entityData.data;

  if (!earningsRecapData) {
    return (
      <S.Root>
        <S.FlexRow>
          <EarningsInsightTypePill
            entityTag={activeEntityTag ?? ''}
            earningsInsight={earningsInsightsData?.find(
              insight => insight.type === 'recap'
            )}
            isAnalyzing
            insightType={'recap'}
            createdAt={earningsData.reporting_date}
            fy={`${earningsData.fiscal_year}`}
            fq={earningsData.fiscal_period}
            releasedDate={earningsData.reporting_date}
          />
        </S.FlexRow>
        <S.TableWrapper>
          <EarningsHighlightsTable earningsData={earningsData} />
        </S.TableWrapper>
        <Shared.EmptyWrapper>
          <Shared.StyledImg
            alt=""
            src="/app/static/images/empty-state/radio-telescope.png"
            height={130}
            width={146}
          />
          <Shared.EmptyTitle>
            {t(
              'earnings:earningsInsight.drawer.earningsRecap.earningsAreBeingAnalyzed'
            )}
          </Shared.EmptyTitle>
        </Shared.EmptyWrapper>
      </S.Root>
    );
  }

  return (
    <S.ContentWrapper>
      <EarningsInsightTypePill
        entityTag={activeEntityTag ?? ''}
        earningsInsight={earningsInsightsData?.[0]}
        insightType="recap"
        createdAt={earningsRecapData.created_at}
        updatedAt={earningsRecapData.updated_at}
        fy={`${earningsData.fiscal_year}`}
        fq={earningsData.fiscal_period}
        releasedDate={earningsData.reporting_date}
      />
      <S.Separator>
        <S.LeftContainer>
          <EarningsInsightHeader entity={entity} />
          <S.ScrollableContainer ref={containerRef}>
            <S.Section ref={recapItems[0].ref} data-testid="overview">
              <RecapOverview
                title={earningsRecapData.title}
                earningsData={earningsData}
                recapData={earningsRecapData}
              />
            </S.Section>
            <S.Section ref={recapItems[1].ref} data-testid="market-reaction">
              <S.HistoricalReactionWrapper>
                <MarketReactionSection
                  entity={entity}
                  earningsId={earningsId ?? ''}
                  reportingDate={earningsData.reporting_date}
                />
              </S.HistoricalReactionWrapper>
            </S.Section>
            <S.Section ref={recapItems[2].ref} data-testid="kpis-in-focus">
              <KpisTable kpisData={earningsRecapData.kpis} />
            </S.Section>
            <S.Section
              ref={recapItems[3].ref}
              data-testid="management-commentary"
            >
              <ManagementCommentarySection
                managementInsights={earningsRecapData.insights}
                quotes={earningsRecapData.quotes}
              />
            </S.Section>
            <S.Section ref={recapItems[4].ref} data-testid="thematic-focus">
              <ThematicFocus
                themes={earningsRecapData.themes}
                otherThemes={earningsRecapData.other_themes}
                title={t(
                  'earningsInsight.drawer.earningsRecap.items.thematicFocusRecap'
                )}
                withPill
              />
            </S.Section>
            <S.Section
              ref={recapItems[5].ref}
              data-testid="related-companies-impact"
            >
              <ErrorBoundaryWrapper>
                <ImpactedCompanies
                  impactedCompanies={earningsRecapData.impacted_companies}
                  variant="secondary"
                />
              </ErrorBoundaryWrapper>
            </S.Section>
            <S.Section ref={recapItems[6].ref} data-testid="peer-comparison">
              <TextSection
                title={t(
                  'earningsInsight.drawer.earningsRecap.items.peerComparison'
                )}
                variant="primary"
                useBulletPoints
                textContent={earningsRecapData.peer_comparison_sector_context}
              />
            </S.Section>
            <S.Section ref={recapItems[7].ref} data-testid="analyst-and-street">
              <BullBearPoints
                title={t(
                  'earningsInsight.drawer.earningsRecap.items.analystAndStreet'
                )}
                data={earningsRecapData.analyst_and_street_reaction}
                variant="primary"
                useBulletPoints
              />
            </S.Section>
            <S.Section
              ref={recapItems[8].ref}
              data-testid="risk-assessment-update"
            >
              <TextSection
                title={t(
                  'earningsInsight.drawer.earningsRecap.items.riskAssessmentUpdate'
                )}
                variant="primary"
                useBulletPoints
                textContent={[
                  ...earningsRecapData.risk_assessment_update.assessments,
                  <span key="upcoming-catalysts">
                    <S.UpcomingCatalysts>
                      {t(
                        'earningsInsight.drawer.earningsRecap.upcomingCatalysts'
                      )}
                    </S.UpcomingCatalysts>
                    <span>
                      {
                        earningsRecapData.risk_assessment_update
                          .upcoming_catalysts
                      }
                    </span>
                  </span>,
                ]}
              />
            </S.Section>
            <S.Section data-testid="citations">
              <DocumentsAndCitations earningsRecapData={earningsRecapData} />
            </S.Section>
          </S.ScrollableContainer>
        </S.LeftContainer>
        <Sidebar
          entityData={entity}
          containerRef={containerRef}
          items={recapItems}
        />
      </S.Separator>
    </S.ContentWrapper>
  );
};
